import { defineStore } from 'pinia'
import { ref } from 'vue'

import { dirOf, i18n, LOCALE_STORAGE_KEY, readStoredLocale, type Locale } from '@/i18n'

export type ToastKind = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: number
  kind: ToastKind
  title: string
  description?: string
  /** ms; 0 keeps the toast until it is dismissed. */
  timeout: number
}

export type Theme = 'dark' | 'light'

const THEME_STORAGE_KEY = 'uobserve.theme'
const SIDEBAR_STORAGE_KEY = 'uobserve.sidebar.collapsed'

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    /* private mode / storage disabled */
  }
  return 'dark'
}

function readStoredSidebar(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

let nextToastId = 1

export const useUiStore = defineStore('ui', () => {
  const toasts = ref<Toast[]>([])
  const theme = ref<Theme>(readStoredTheme())
  const sidebarCollapsed = ref<boolean>(readStoredSidebar())
  const locale = ref<Locale>(readStoredLocale())
  /** Mobile/narrow layouts render the sidebar as an overlay. */
  const sidebarOpenMobile = ref(false)

  function applyTheme(): void {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme.value)
    }
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme.value)
    } catch {
      /* nothing we can do; the in-memory theme still applies */
    }
  }

  function setTheme(next: Theme): void {
    theme.value = next
    applyTheme()
  }

  /** Switches the language everywhere at once: messages, <html lang dir>, and the stored choice. */
  function setLocale(next: Locale): void {
    locale.value = next
    i18n.global.locale.value = next
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', next)
      document.documentElement.setAttribute('dir', dirOf(next))
    }
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next)
    } catch {
      /* the in-memory choice still applies */
    }
  }

  function toggleLocale(): void {
    setLocale(locale.value === 'ar' ? 'en' : 'ar')
  }

  function toggleTheme(): void {
    setTheme(theme.value === 'dark' ? 'light' : 'dark')
  }

  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarCollapsed.value))
    } catch {
      /* ignore */
    }
  }

  function dismissToast(id: number): void {
    toasts.value = toasts.value.filter((toast) => toast.id !== id)
  }

  function pushToast(input: {
    kind?: ToastKind
    title: string
    description?: string
    timeout?: number
  }): number {
    const id = nextToastId++
    const toast: Toast = {
      id,
      kind: input.kind ?? 'info',
      title: input.title,
      timeout: input.timeout ?? (input.kind === 'error' ? 9000 : 5000),
    }
    if (input.description) toast.description = input.description
    toasts.value = [...toasts.value, toast]

    if (toast.timeout > 0) {
      setTimeout(() => dismissToast(id), toast.timeout)
    }
    return id
  }

  // Apply on store creation so a hard reload cannot desync <html> from the stored value.
  applyTheme()
  setLocale(locale.value)

  return {
    toasts,
    theme,
    locale,
    setLocale,
    toggleLocale,
    sidebarCollapsed,
    sidebarOpenMobile,
    setTheme,
    toggleTheme,
    toggleSidebar,
    pushToast,
    dismissToast,
  }
})
